import {
  ActionIcon,
  Button,
  Group,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { ReactElement } from "react";

export enum PresetParameterType {
  INT = "INT",
  FLOAT = "FLOAT",
  STRING = "STRING",
  BOOLEAN = "BOOLEAN",
}

export interface PresetParameterDraft {
  name: string;
  type: PresetParameterType;
  required: boolean;
  defaultValue: string | null;
  description: string | null;
}

export const PRESET_PARAMETER_MAX_COUNT = 50;
export const PRESET_PARAMETER_NAME_MAX_LENGTH = 64;
export const PRESET_PARAMETER_DESCRIPTION_MAX_LENGTH = 500;
export const PRESET_PARAMETER_DEFAULT_MAX_LENGTH = 500;

interface PresetParameterTypeOption {
  label: string;
  value: PresetParameterType;
}

const PRESET_PARAMETER_TYPE_OPTIONS: PresetParameterTypeOption[] = [
  { label: "Integer", value: PresetParameterType.INT },
  { label: "Float", value: PresetParameterType.FLOAT },
  { label: "String", value: PresetParameterType.STRING },
  { label: "Boolean", value: PresetParameterType.BOOLEAN },
];

const isPresetParameterType = (value: string): value is PresetParameterType =>
  PRESET_PARAMETER_TYPE_OPTIONS.some((option) => option.value === value);

const createBlankParameter = (): PresetParameterDraft => ({
  name: "",
  type: PresetParameterType.STRING,
  required: false,
  defaultValue: null,
  description: null,
});

interface PresetParametersEditorProps {
  onChange: (value: PresetParameterDraft[]) => void;
  value?: PresetParameterDraft[] | null;
}

export const PresetParametersEditor = ({
  onChange,
  value,
}: PresetParametersEditorProps): ReactElement => {
  const parameters = value ?? [];

  const addParameter = () => {
    if (parameters.length >= PRESET_PARAMETER_MAX_COUNT) return;
    onChange([...parameters, createBlankParameter()]);
  };

  const removeParameter = (index: number) => {
    onChange(parameters.filter((_, i) => i !== index));
  };

  const updateParameter = <K extends keyof PresetParameterDraft>(
    index: number,
    field: K,
    fieldValue: PresetParameterDraft[K],
  ) => {
    onChange(
      parameters.map((param, i) =>
        i === index ? { ...param, [field]: fieldValue } : param,
      ),
    );
  };

  return (
    <Stack gap="sm">
      {parameters.length === 0 && (
        <Text size="sm" c="dimmed">
          No parameters defined. Add a parameter to expose configurable inputs
          to consumers of this preset.
        </Text>
      )}
      {parameters.map((param, index) => (
        <Stack
          key={index}
          gap="xs"
          p="sm"
          style={(theme) => ({
            border: `1px solid ${theme.colors.dark[5]}`,
            borderRadius: theme.radius.md,
            backgroundColor: theme.colors.dark[7],
          })}
        >
          <Group gap="xs" wrap="nowrap" align="flex-end">
            <TextInput
              label={index === 0 ? "Name" : undefined}
              placeholder="parameter_name"
              value={param.name}
              onChange={(event) =>
                updateParameter(index, "name", event.currentTarget.value)
              }
              maxLength={PRESET_PARAMETER_NAME_MAX_LENGTH}
              style={{ flex: 2 }}
            />
            <Select
              label={index === 0 ? "Type" : undefined}
              data={PRESET_PARAMETER_TYPE_OPTIONS}
              value={param.type}
              onChange={(next) => {
                if (next && isPresetParameterType(next)) {
                  updateParameter(index, "type", next);
                }
              }}
              allowDeselect={false}
              style={{ flex: 1 }}
            />
            <Switch
              label={index === 0 ? "Required" : undefined}
              checked={param.required}
              onChange={(event) =>
                updateParameter(index, "required", event.currentTarget.checked)
              }
            />
            <ActionIcon
              color="rose"
              variant="subtle"
              onClick={() => removeParameter(index)}
              aria-label="Remove parameter"
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
          <Group gap="xs" wrap="nowrap" align="flex-start">
            <TextInput
              label="Default value"
              placeholder="Optional default"
              value={param.defaultValue ?? ""}
              onChange={(event) =>
                updateParameter(
                  index,
                  "defaultValue",
                  event.currentTarget.value === ""
                    ? null
                    : event.currentTarget.value,
                )
              }
              maxLength={PRESET_PARAMETER_DEFAULT_MAX_LENGTH}
              style={{ flex: 1 }}
            />
            <TextInput
              label="Description"
              placeholder="What does this parameter do?"
              value={param.description ?? ""}
              onChange={(event) =>
                updateParameter(
                  index,
                  "description",
                  event.currentTarget.value === ""
                    ? null
                    : event.currentTarget.value,
                )
              }
              maxLength={PRESET_PARAMETER_DESCRIPTION_MAX_LENGTH}
              style={{ flex: 2 }}
            />
          </Group>
        </Stack>
      ))}
      {parameters.length >= PRESET_PARAMETER_MAX_COUNT && (
        <Text size="xs" c="dimmed">
          Maximum of {PRESET_PARAMETER_MAX_COUNT} parameters reached.
        </Text>
      )}
      <Button
        leftSection={<IconPlus size={14} />}
        variant="light"
        size="xs"
        onClick={addParameter}
        disabled={parameters.length >= PRESET_PARAMETER_MAX_COUNT}
        style={{ alignSelf: "flex-start" }}
      >
        Add parameter
      </Button>
    </Stack>
  );
};
